import { Check, ChevronDown, ChevronUp, Minus, X } from 'lucide-react';
import React, { useState } from 'react';
import { UsersFilterShape } from '../../../core/models/user';
import { ApplyBtn, ClearBtn } from '../../../core/components/Button';
import { Card } from '../../../core/components/Card';
import { Input, Label } from '../../../core/components/FormElements';
import './UsersFilter.css';

interface UsersFilterProps {
    value: UsersFilterShape;
    onChange: (v: UsersFilterShape) => void;
    onApply: () => void;
    onClear: () => void;
    className?: string;
}

type TriState = boolean | null | undefined;

const cycleTri = (cur: TriState): TriState => {
    const v = cur === undefined ? null : cur;
    return v === null ? true : v === true ? false : null;
};

const triStateClass = (v: TriState) =>
    v === null || v === undefined ? 'null' : v ? 'true' : 'false';

const TriChip: React.FC<{
    label: string;
    value: TriState;
    onClick: () => void;
}> = ({ label, value, onClick }) => {
    const state = triStateClass(value);
    const icon =
        state === 'true'  ? <Check size={11} strokeWidth={3} /> :
        state === 'false' ? <X size={11} strokeWidth={3} /> :
                            <Minus size={11} strokeWidth={3} />;

    return (
        <button
            type="button"
            className={`filter-chip filter-chip--${state}`}
            onClick={onClick}
        >
            <span className="filter-chip__icon">{icon}</span>
            {label}
        </button>
    );
};

const UsersFilter: React.FC<UsersFilterProps> = ({ value, onChange, onApply, onClear, className = '' }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <Card className={`users-filter-panel ${className}`}>
            <div className="filter-header">
                <span className="filter-header__title">Фильтры</span>
                <div className="filter-header__actions">
                    {isExpanded && (
                        <>
                            <ApplyBtn size="sm" onClick={onApply} />
                            <ClearBtn size="sm" onClick={onClear} />
                        </>
                    )}
                    <button
                        type="button"
                        className="filter-collapse-btn"
                        onClick={() => setIsExpanded(v => !v)}
                        aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
                    >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="filter-body">
                    <div className="filter-section">
                        <span className="filter-section__label">Подписка</span>
                        <div className="filter-chips">
                            <TriChip
                                label="Оплачена"
                                value={value.hasPayedSubscription}
                                onClick={() => onChange({ ...value, hasPayedSubscription: cycleTri(value.hasPayedSubscription) })}
                            />
                            <TriChip
                                label="Активна"
                                value={value.hasSubscription}
                                onClick={() => onChange({ ...value, hasSubscription: cycleTri(value.hasSubscription) })}
                            />
                            <TriChip
                                label="Истекла"
                                value={value.subIsOver}
                                onClick={() => onChange({ ...value, subIsOver: cycleTri(value.subIsOver) })}
                            />
                        </div>
                    </div>

                    <div className="filter-section">
                        <span className="filter-section__label">Поиск</span>
                        <div className="filter-inputs-grid">
                            <div className="filter-input-field">
                                <Label>Истекает через (дней)</Label>
                                <Input
                                    type="number"
                                    value={value.subExpiresInDays ?? ''}
                                    onChange={(e) => onChange({
                                        ...value,
                                        subExpiresInDays: e.target.value ? parseInt(e.target.value) : undefined,
                                    })}
                                    placeholder="Кол-во дней"
                                />
                            </div>
                            <div className="filter-input-field">
                                <Label>TG IDs</Label>
                                <Input
                                    type="text"
                                    value={(value.inTgIds || []).join(',')}
                                    onChange={(e) => onChange({
                                        ...value,
                                        inTgIds: e.target.value
                                            ? e.target.value.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n))
                                            : undefined,
                                    })}
                                    placeholder="123, 456, ..."
                                />
                            </div>
                            <div className="filter-input-field">
                                <Label>User UUIDs</Label>
                                <Input
                                    type="text"
                                    value={(value.inUserIds || []).join(',')}
                                    onChange={(e) => onChange({
                                        ...value,
                                        inUserIds: e.target.value
                                            ? e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            : undefined,
                                    })}
                                    placeholder="uuid1, uuid2, ..."
                                />
                            </div>
                            <div className="filter-input-field">
                                <Label>TG Names</Label>
                                <Input
                                    type="text"
                                    value={(value.inTgName || []).join(',')}
                                    onChange={(e) => onChange({
                                        ...value,
                                        inTgName: e.target.value
                                            ? e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                            : undefined,
                                    })}
                                    placeholder="@name1, @name2, ..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default UsersFilter;
